import { CustomDatePipe } from '@abovo/shared-angular-utils';
import { ACL_TARGET_APPROVALS, AclRestrictDirective } from '@abovo/shared-auth';
import { ApprovalRequestComment, Restriction } from '@abovo/shared-models';
import { InitialsAvatarComponent, SkeletonLoaderModule, SkeletonTypes, TextInputComponent } from '@abovo/shared-ui-components';
import { isDefined } from '@abovo/shared-utils';
import { NgClass, NgTemplateOutlet } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, effect, ElementRef, inject, input, output, signal, viewChild } from '@angular/core';
import { FormBuilder, ReactiveFormsModule } from '@angular/forms';
import { MatButton, MatIconButton } from '@angular/material/button';
import { MatCheckbox } from '@angular/material/checkbox';
import { MatDivider } from '@angular/material/list';
import { MatMenu, MatMenuContent, MatMenuItem, MatMenuTrigger } from '@angular/material/menu';
import { FaIconComponent } from '@fortawesome/angular-fontawesome';
import { TranslatePipe } from '@ngx-translate/core';

export interface CommentSubmitPayload {
   content: string;
   slideNumber?: number | null;
   parentId?: number;
}

export interface CommentEditPayload {
   commentId: number;
   content: string;
}

/** UI-only wrapper for orphaned-reply tombstones. Never sent to/from the backend. */
export type FeedbackCommentNode = ApprovalRequestComment & { isTombstone?: boolean };

@Component({
   selector: 'webvis-feature-approval-feedback-comments',
   templateUrl: './approval-feedback-comments.component.html',
   styleUrls: ['./approval-feedback-comments.component.scss'],
   changeDetection: ChangeDetectionStrategy.OnPush,
   host: { class: 'contents' },
   imports: [
      TranslatePipe,
      ReactiveFormsModule,
      TextInputComponent,
      MatButton,
      MatCheckbox,
      MatMenu,
      MatMenuItem,
      MatMenuTrigger,
      MatMenuContent,
      MatIconButton,
      FaIconComponent,
      InitialsAvatarComponent,
      CustomDatePipe,
      SkeletonLoaderModule,
      NgTemplateOutlet,
      NgClass,
      MatDivider,
      AclRestrictDirective,
   ],
})
export class ApprovalFeedbackCommentsComponent {
   private readonly fb = inject(FormBuilder);
   private readonly elementRef = inject(ElementRef);

   readonly replyToInput = viewChild<TextInputComponent>('replyInput');
   readonly editInput = viewChild<TextInputComponent>('editInput');

   readonly comments = input<FeedbackCommentNode[]>([]);
   readonly currentSlide = input(1);
   readonly loading = input(false);
   readonly currentUserId = input<number | null>(null);

   readonly commentSubmitted = output<CommentSubmitPayload>();
   readonly commentEditSubmitted = output<CommentEditPayload>();
   readonly commentDeleted = output<number>();
   readonly goToSlide = output<number>();

   readonly skeletonType = SkeletonTypes.list;

   readonly commentMode = signal<'slide' | 'general'>('slide');
   readonly slideFilter = signal<number | null | 'all'>('all');
   readonly newCommentIds = signal<Set<number>>(new Set());

   readonly commentRestriction: Restriction = {
      target: ACL_TARGET_APPROVALS,
      operation: 'execute',
      description: 'ApprovalFeedbackCommentsComponent create comment',
   };

   /** Tracks previous comment IDs so the effect can diff against them to detect new additions */
   private knownCommentIds = new Set<number>();

   private readonly highlightNewCommentsEffect = effect(() => {
      const comments = this.comments();
      const allIds = this.collectAllIds(comments);

      const newIds = [...allIds].filter((id) => !this.knownCommentIds.has(id));
      this.knownCommentIds = allIds;

      if (newIds.length > 0 && this.knownCommentIds.size > newIds.length) {
         this.newCommentIds.set(new Set(newIds));
         setTimeout(() => this.newCommentIds.set(new Set()), 1500);
         requestAnimationFrame(() => {
            const el = this.elementRef.nativeElement.querySelector(`[data-comment-id="${newIds[0]}"]`);
            el?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
         });
      }
   });

   private readonly syncSlideEffect = effect(() => {
      const slide = this.currentSlide();
      const mode = this.commentMode();
      this.commentForm.controls.slideNumber.setValue(mode === 'slide' ? slide : null);
   });

   readonly replyingTo = signal<number | null>(null);
   readonly editingCommentId = signal<number | null>(null);

   /** Tracks which parent comment has a reply being submitted */
   readonly loadingReplyTo = signal<number | null>(null);
   readonly loadingEditId = signal<number | null>(null);
   readonly loadingDeleteId = signal<number | null>(null);

   readonly commentForm = this.fb.group({
      slideNumber: this.fb.control<number | null>(null),
      content: this.fb.nonNullable.control(''),
   });

   readonly replyForm = this.fb.nonNullable.control('');
   readonly editForm = this.fb.control('');

   readonly uniqueSlideNumbers = computed(() => {
      const slides = this.comments().map((c) => c.slide_number);
      const unique = [...new Set(slides)];
      return unique.sort((a, b) => {
         if (a === null) return -1;
         if (b === null) return 1;
         return a - b;
      });
   });

   readonly filteredComments = computed(() => {
      const filter = this.slideFilter();
      const comments = this.comments();
      let items: ApprovalRequestComment[];
      if (filter === 'all') {
         items = comments;
      } else if (filter === null) {
         items = comments.filter((comment) => !isDefined(comment.slide_number));
      } else {
         items = comments.filter((comment) => comment.slide_number === filter);
      }

      return items
         .toSorted((a, b) => {
            const aIsGeneral = a.slide_number === null ? 0 : 1;
            const bIsGeneral = b.slide_number === null ? 0 : 1;
            if (aIsGeneral !== bIsGeneral) return aIsGeneral - bIsGeneral;

            const slideDiff = (a.slide_number ?? 0) - (b.slide_number ?? 0);
            if (slideDiff !== 0) return slideDiff;

            return a.create_date_time.getTime() - b.create_date_time.getTime();
         })
         .map((comment) => ({
            ...comment,
            children: this.flattenChildren(comment.children ?? []),
         }));
   });

   private collectAllIds(comments: ApprovalRequestComment[]): Set<number> {
      const flatten = (items: ApprovalRequestComment[]): number[] => items.flatMap((item) => [item.id, ...flatten(item.children ?? [])]);
      return new Set(flatten(comments));
   }

   private flattenChildren(children: ApprovalRequestComment[]): ApprovalRequestComment[] {
      return [...children]
         .sort((a, b) => a.create_date_time.getTime() - b.create_date_time.getTime())
         .flatMap((child) => [{ ...child, children: [] }, ...this.flattenChildren(child.children ?? [])]);
   }

   isOwnComment(comment: FeedbackCommentNode): boolean {
      if (comment.isTombstone) return false;
      return comment.commentor?.id === this.currentUserId();
   }

   submitComment(): void {
      if (this.commentForm.invalid) return;
      this.loadingReplyTo.set(null);
      const { slideNumber, content } = this.commentForm.getRawValue();
      this.commentSubmitted.emit({
         content,
         slideNumber: slideNumber,
      });
      this.commentForm.controls.content.reset();
   }

   submitReply(parentId: number): void {
      if (this.replyForm.invalid) return;
      this.loadingReplyTo.set(parentId);
      this.commentSubmitted.emit({
         content: this.replyForm.value,
         parentId,
      });
      this.replyForm.reset();
      this.replyingTo.set(null);
   }

   onCtrlEnter(event: KeyboardEvent, type: 'comment' | 'reply' | 'edit', parentId?: number): void {
      if (event.ctrlKey && event.key === 'Enter') {
         event.preventDefault();
         if (type === 'comment') {
            this.submitComment();
         } else if (type === 'reply' && parentId) {
            this.submitReply(parentId);
         } else if (type === 'edit' && parentId) {
            this.submitEdit(parentId);
         }
      }
   }

   setSlideFilter(slide: number | null | 'all'): void {
      this.slideFilter.set(slide);
   }

   startReply(commentId: number): void {
      this.cancelEdit();
      this.replyingTo.set(commentId);
      this.replyForm.reset();
      requestAnimationFrame(() => this.replyToInput()?.focus());
   }

   cancelReply(): void {
      this.replyingTo.set(null);
      this.replyForm.reset();
   }

   startEdit(comment: ApprovalRequestComment): void {
      this.cancelReply();
      this.editingCommentId.set(comment.id);
      this.editForm.setValue(comment.content);
      requestAnimationFrame(() => this.editInput()?.focus());
   }

   cancelEdit(): void {
      this.editingCommentId.set(null);
      this.editForm.reset();
   }

   submitEdit(commentId: number): void {
      const content = this.editForm.value ?? '';
      if (content.trim().length < 3) return;
      this.loadingEditId.set(commentId);
      this.editingCommentId.set(null);
      this.editForm.reset();
      this.commentEditSubmitted.emit({ commentId, content });
   }

   requestDelete(commentId: number): void {
      this.commentDeleted.emit(commentId);
      // This should be set by the parent after the delete action has been confirmed
      // this.loadingDeleteId.set(commentId);
   }

   public restoreEdit(commentId: number, content: string): void {
      this.loadingEditId.set(null);
      this.editingCommentId.set(commentId);
      this.editForm.setValue(content);
   }

   public clearEditState(): void {
      this.loadingEditId.set(null);
      this.editingCommentId.set(null);
      this.editForm.reset();
   }

   public clearDeleteState(): void {
      this.loadingDeleteId.set(null);
   }

   public clearReplyState(): void {
      this.loadingReplyTo.set(null);
   }

   onSlideClick(slideNumber: number): void {
      this.goToSlide.emit(slideNumber);
   }

   public restorePayload(payload: CommentSubmitPayload): void {
      if (payload.parentId) {
         this.replyingTo.set(payload.parentId);
         this.replyForm.setValue(payload.content);
      } else {
         this.commentMode.set(payload.slideNumber !== null ? 'slide' : 'general');
         this.commentForm.patchValue({ content: payload.content, slideNumber: payload.slideNumber });
      }
   }
}
